import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Calendar, 
  Clock, 
  User, 
  Tag,
  Search,
  BookOpen,
  TrendingUp,
  Lightbulb,
  FileText,
  CheckCircle,
  Loader2
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
// Blog categories
const categories = [
  { name: "All", count: 12 },
  { name: "Getting Started", count: 4 },
  { name: "Advanced Strategies", count: 3 },
  { name: "Case Studies", count: 2 },
  { name: "Industry News", count: 3 }
];

// Blog posts data
const blogPosts = [
  {
    id: 1,
    title: "Complete Guide to Pay-Per-Call Marketing in 2024",
    excerpt: "Learn everything you need to know about pay-per-call marketing, from basics to advanced strategies that top publishers use to generate thousands of calls.",
    category: "Getting Started",
    author: "Digital Master Media",
    date: "Dec 10, 2024",
    readTime: "12 min read",
    featured: true,
    image: "https://images.unsplash.com/photo-1553484771-371a605b060b?w=800&h=400&fit=crop"
  },
  {
    id: 2,
    title: "How to Set Up Google Ads for Pay-Per-Call Campaigns",
    excerpt: "Step-by-step tutorial on creating high-converting Google Ads campaigns specifically optimized for generating phone calls.",
    category: "Getting Started",
    author: "Digital Master Media",
    date: "Dec 5, 2024",
    readTime: "8 min read",
    featured: false,
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=400&fit=crop"
  },
  {
    id: 3,
    title: "Case Study: How One Publisher Generated $50K/Month",
    excerpt: "Real success story of a publisher who scaled from zero to $50,000 monthly revenue using our pay-per-call network.",
    category: "Case Studies",
    author: "Digital Master Media",
    date: "Nov 28, 2024",
    readTime: "10 min read",
    featured: true,
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=400&fit=crop"
  },
  {
    id: 4,
    title: "Understanding Call Tracking: Ringba vs Invoca vs CallRail",
    excerpt: "Comprehensive comparison of the top call tracking platforms and how to choose the right one for your campaigns.",
    category: "Advanced Strategies",
    author: "Digital Master Media",
    date: "Nov 20, 2024",
    readTime: "15 min read",
    featured: false,
    image: "https://images.unsplash.com/photo-1596526131083-e8c633c948d2?w=800&h=400&fit=crop"
  },
  {
    id: 5,
    title: "Top 10 Home Services Verticals for Pay-Per-Call in 2024",
    excerpt: "Discover the most profitable home services niches and learn which verticals offer the highest payouts and conversion rates.",
    category: "Industry News",
    author: "Digital Master Media",
    date: "Nov 15, 2024",
    readTime: "7 min read",
    featured: false,
    image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800&h=400&fit=crop"
  },
  {
    id: 6,
    title: "Facebook Ads for Pay-Per-Call: A Complete Strategy Guide",
    excerpt: "Master Facebook advertising for lead generation and learn how to create campaigns that drive high-intent phone calls.",
    category: "Getting Started",
    author: "Digital Master Media",
    date: "Nov 10, 2024",
    readTime: "11 min read",
    featured: false,
    image: "https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=800&h=400&fit=crop"
  },
  {
    id: 7,
    title: "Scaling Your Pay-Per-Call Business: From $1K to $10K/Month",
    excerpt: "Proven strategies and tactics to scale your pay-per-call revenue from a side hustle to a full-time income.",
    category: "Advanced Strategies",
    author: "Digital Master Media",
    date: "Nov 5, 2024",
    readTime: "14 min read",
    featured: false,
    image: "https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=800&h=400&fit=crop"
  },
  {
    id: 8,
    title: "TCPA Compliance: What Every Publisher Needs to Know",
    excerpt: "Essential guide to staying compliant with TCPA regulations and avoiding costly violations in your call campaigns.",
    category: "Industry News",
    author: "Digital Master Media",
    date: "Oct 28, 2024",
    readTime: "9 min read",
    featured: false,
    image: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&h=400&fit=crop"
  }
];

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const BlogPage = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [email, setEmail] = useState("");
  const [subscribeStatus, setSubscribeStatus] = useState({ loading: false, success: false, error: "" });
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubscribeStatus({ loading: true, success: false, error: "" });
    
    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubscribeStatus({ loading: false, success: true, error: "" });
        setEmail("");
      } else {
        setSubscribeStatus({ loading: false, success: false, error: data.detail || "Failed to subscribe" });
      }
    } catch (error) {
      setSubscribeStatus({ loading: false, success: false, error: "Something went wrong. Please try again." });
    }
  };

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPosts = blogPosts.filter(post => post.featured);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0f172a] text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 relative">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
              <BookOpen className="w-4 h-4" />
              Resources & Guides
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Pay-Per-Call <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">Blog</span>
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
              Expert tips, strategies, and industry insights to help you succeed in pay-per-call marketing
            </p>
            
            {/* Search Bar */}
            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Posts */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 mb-8">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Featured Articles</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {featuredPosts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all"
              >
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Featured
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {post.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {post.readTime}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-2">{post.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-blue-600 font-medium">{post.category}</span>
                    <span className="text-blue-600 font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                      Read More <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Categories & Posts */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sidebar */}
            <div className="lg:w-64 flex-shrink-0">
              <div className="sticky top-32">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Categories</h3>
                <div className="space-y-2">
                  {categories.map((category) => (
                    <button
                      key={category.name}
                      onClick={() => setSelectedCategory(category.name)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                        selectedCategory === category.name
                          ? "bg-blue-600 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      <span>{category.name}</span>
                      <span className={`text-sm ${
                        selectedCategory === category.name ? "text-blue-200" : "text-gray-500"
                      }`}>
                        {category.count}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Quick Links */}
                <div className="mt-8 p-6 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl text-white">
                  <Lightbulb className="w-8 h-8 mb-4" />
                  <h4 className="font-bold mb-2">Ready to Start?</h4>
                  <p className="text-sm text-white/80 mb-4">
                    Apply now and start earning with pay-per-call today.
                  </p>
                  <Link 
                    to="/apply"
                    className="block w-full bg-white text-blue-600 font-semibold py-2 px-4 rounded-lg text-center hover:bg-gray-100 transition-colors"
                  >
                    Publisher Sign Up
                  </Link>
                </div>
              </div>
            </div>

            {/* Posts Grid */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedCategory === "All" ? "All Articles" : selectedCategory}
                </h2>
                <span className="text-gray-500">{filteredPosts.length} articles</span>
              </div>

              {filteredPosts.length === 0 ? (
                <div className="text-center py-16 bg-gray-50 rounded-2xl">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No articles found</h3>
                  <p className="text-gray-600">Try adjusting your search or category filter</p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-8">
                  {filteredPosts.map((post, index) => (
                    <motion.article
                      key={post.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl hover:border-blue-200 transition-all"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img 
                          src={post.image} 
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-medium px-3 py-1 rounded-full">
                            {post.category}
                          </span>
                        </div>
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {post.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {post.readTime}
                          </span>
                        </div>
                        <h3 className="font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
                          {post.title}
                        </h3>
                        <p className="text-sm text-gray-600 line-clamp-2 mb-4">{post.excerpt}</p>
                        <span className="text-blue-600 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                          Read Article <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </motion.article>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Stay Updated with Industry Insights
          </h2>
          <p className="text-white/80 mb-8">
            Get the latest pay-per-call tips, strategies, and news delivered to your inbox
          </p>
          
          {subscribeStatus.success ? (
            <div className="flex items-center justify-center gap-3 text-white bg-white/20 rounded-full py-4 px-8 max-w-lg mx-auto">
              <CheckCircle className="w-6 h-6" />
              <span className="font-semibold">Successfully subscribed! Check your inbox.</span>
            </div>
          ) : (
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 justify-center max-w-lg mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="flex-1 px-6 py-4 rounded-full focus:outline-none focus:ring-2 focus:ring-white/50"
                data-testid="newsletter-email"
              />
              <button 
                type="submit"
                disabled={subscribeStatus.loading}
                className="bg-white text-blue-600 font-bold px-8 py-4 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
                data-testid="newsletter-submit"
              >
                {subscribeStatus.loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Subscribing...
                  </>
                ) : (
                  "Subscribe"
                )}
              </button>
            </form>
          )}
          
          {subscribeStatus.error && (
            <p className="text-red-200 mt-4">{subscribeStatus.error}</p>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;
